import { useEnum } from "@/api/services/global/enum";
import { useGetLanguageTestList } from "@/api/services/global/master";
import { TTalentListOutputDto } from "@/api/services/main/talent/dtos/get-talent-list.dto";
import { IconActionStar } from "@/assets/icons";
import { Table, TableSorting } from "@/components/elements/Table";
import { DATE_FORMAT } from "@/constants/datetime";
import { EMPTY_STR } from "@/constants/string";
import { TalentStatusTag } from "@/features/talent/components/talent-common/TalentStatusTag";
import { useTable } from "@/hooks/useTable";
import { AppRoutes } from "@/routes/config";
import { CaseMatchStatusOptions } from "@/types/enum";
import { dayjs } from "@/utils/dayjs";
import { findLabelByValue } from "@/utils/object";
import { truncateWithEllipsis } from "@/utils/string";
import { getDynamicRoute } from "@/utils/url";
import { Box, Stack, Typography } from "@mui/material";
import { MRT_ColumnDef, MRT_RowSelectionState } from "material-react-table";
import { FC, useMemo, useEffect, useRef, useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CaseMatchStatusTag } from "@/features/talent/components/talent-common/CaseMatchStatusTag";

type TTalentListTableProps = {
  rows: TTalentListOutputDto[];
  isLoading: boolean;
  onSelectionChange?: (selectedIds: number[], isAllSelected: boolean) => void;
  selectedIds?: number[];
};

export const TalentListTable: FC<TTalentListTableProps> = ({ rows, isLoading, onSelectionChange, selectedIds = [] }) => {
  const { data: genderOptions } = useEnum("EGender");
  const { data: recruitmentStatusOptions } = useEnum("ERecruitmentStatus");
  const { languageTestLevelOptions } = useGetLanguageTestList();
  const navigate = useNavigate();

  // Track if user clicked "Select All" button
  const userClickedSelectAll = useRef(false);
  const prevIsAllRowsSelected = useRef(false);
  const allRowIdsWhenSelectAll = useRef<number[]>([]);
  const rowsRef = useRef(rows);
  const isInitialMount = useRef(true);
  const isSyncingSelection = useRef(false);
  const [heightTable] = useState<string>("calc(100vh - 460px)");

  const columns = useMemo<MRT_ColumnDef<TTalentListOutputDto>[]>(
    () => [
      {
        id: "talentNumber",
        header: "人材管理番号",
        Cell: ({ row }) => {
          return (
            <Stack direction="row" alignItems="center">
              <Typography variant="body2" sx={{ "&&": { p: 0 } }}>
                {row.original?.talentNumber ? truncateWithEllipsis(row.original.talentNumber, 20) : EMPTY_STR.TEXT}
              </Typography>
            </Stack>
          );
        },
        accessorKey: "talentNumber",
      },
      {
        id: "fullName",
        accessorKey: "fullName",
        Cell: ({ row }) => {
          return (
            <Stack direction="row" alignItems="center">
              {row.original.isRecommendedTalent && (
                <Box sx={{ flexShrink: 0, minWidth: "24px", minHeight: "25px", display: "flex", alignItems: "center" }}>
                  <IconActionStar width={24} height={25} />
                </Box>
              )}
              <Typography variant="body2" sx={{ "&&": { p: 0 } }}>
                {row.original?.fullName ? truncateWithEllipsis(row.original.fullName, 20) : EMPTY_STR.TEXT}
              </Typography>
            </Stack>
          );
        },
        header: "氏名",
        Header: <TableSorting name="talent.fullName">氏名</TableSorting>,
        muiTableHeadCellProps: {
          sx: { maxWidth: 183 },
        },
        muiTableBodyCellProps: {
          sx: { maxWidth: 183 },
        },
      },
      {
        id: "nationalityName",
        accessorKey: "nationality.nationalityName",
        Cell: ({ row }) => {
          return (
            <Stack direction="row" alignItems="center">
              <Typography variant="body2" sx={{ "&&": { p: 0 } }}>
                {row.original?.nationality?.nationalityName || EMPTY_STR.TEXT}
              </Typography>
            </Stack>
          );
        },
        header: "国籍",
        Header: <TableSorting name="nationality.nationalityName">国籍</TableSorting>,
      },
      {
        id: "gender",
        accessorKey: "gender",
        header: "性別",
        Cell: ({ row }) => findLabelByValue(genderOptions, row.original?.gender) || EMPTY_STR.TEXT,
      },
      {
        id: "recruitmentStatus",
        header: "ステータス",
        accessorKey: "recruitmentStatus",
        Cell: ({ row }) => (
          <TalentStatusTag
            status={row.original?.recruitmentStatus}
            title={findLabelByValue(recruitmentStatusOptions, row.original.recruitmentStatus)}
            variant="bold"
          />
        ),
        Header: <TableSorting name="talent.recruitmentStatus">ステータス</TableSorting>,
      },
      {
        id: "caseMatchStatus",
        header: "最新進捗",
        accessorKey: "caseMatchStatus",
        Cell: ({ row }) => {
          return (
            <CaseMatchStatusTag
              status={row.original?.caseMatchStatus ?? -1}
              title={findLabelByValue(CaseMatchStatusOptions, row.original?.caseMatchStatus) || "提案なし"}
              variant="bold"
            />
          );
        },
        Header: <TableSorting name="caseMatchStatus">最新進捗</TableSorting>,
      },
      {
        id: "residenceStatusId",
        header: "在留資格",
        Cell: ({ row }) => row.original?.residenceStatus?.residenceStatus || EMPTY_STR.TEXT,
        Header: <TableSorting name="residenceStatus.residenceStatus">在留資格</TableSorting>,
      },
      {
        id: "languageCertificates",
        header: "日本語能力",
        Cell: ({ row }) => {
          if (!row.original.languageCertificates || row.original.languageCertificates.length === 0) {
            return EMPTY_STR.TEXT;
          }
          const languageTestLevelIds: (number | null)[] = [];
          row.original.languageCertificates.forEach((cert) => {
            if (cert?.languageTestLevel?.id) {
              languageTestLevelIds.push(cert.languageTestLevel.id);
            } else if (cert?.languageTestId) {
              languageTestLevelIds.push(-cert.languageTestId);
            }
          });

          const labels = languageTestLevelIds
            .map((id) => {
              if (id === null || id === undefined) return null;
              const option = languageTestLevelOptions.find((opt) => String(opt.value) === String(id));
              return option?.label ?? null;
            })
            .filter((label) => label !== null);

          const label = labels.length > 0 ? labels.join("、") : "";

          return label ? truncateWithEllipsis(label, 20) : EMPTY_STR.TEXT;
        },
        Header: <TableSorting name="languageTestLevel.level">日本語能力</TableSorting>,
      },
      {
        id: "companySpecificIndustry",
        header: "分野",
        Cell: ({ row }) => {
          return row.original.companySpecificIndustry?.companySpecificIndustry || EMPTY_STR.TEXT;
        },
        Header: <TableSorting name="companySpecificIndustry.companySpecificIndustry">分野</TableSorting>,
        muiTableHeadCellProps: {
          sx: { maxWidth: 120 },
        },
        muiTableBodyCellProps: {
          sx: { maxWidth: 120 },
        },
      },
      {
        id: "personInChargeId",
        header: "担当者",
        accessorKey: "personInCharge.fullName",
        Cell: ({ row }) => {
          return (
            <Typography variant="body2" sx={{ "&&": { p: 0 } }}>
              {row.original?.personInCharge?.fullName ? truncateWithEllipsis(row.original.personInCharge.fullName, 20) : EMPTY_STR.TEXT}
            </Typography>
          );
        },
        Header: <TableSorting name="personInCharge.fullName">担当者</TableSorting>,
      },
      {
        id: "createdAt",
        header: "登録日",
        Cell: ({ row }) => dayjs(row.original.createdAt).format(DATE_FORMAT),
        Header: <TableSorting name="talent.createdAt">登録日</TableSorting>,
      },
    ],
    [genderOptions, recruitmentStatusOptions, languageTestLevelOptions],
  );

  const toEditRoute = useCallback(
    (talentId: number) => {
      const route = getDynamicRoute(AppRoutes.talentDetail, { talentId: talentId });
      navigate(route);
    },
    [navigate],
  );

  const { table } = useTable({
    columns,
    rows,
    enableRowSelection: true,
    getRowId: (row) => row.id.toString(),
    pinningColumnsLeft: ["mrt-row-select"],
    muiTableContainerProps: {
      sx: { tableLayout: "fixed" },
    },
    defaultColumn: {
      minSize: 50,
      maxSize: Number.MAX_VALUE,
      size: 1,
    },
    onClickRow: (row) => toEditRoute(row.id),
    renderEmptyRowsFallback: ({}) => <Box></Box>,
  });

  const ids = useMemo(() => table.getSelectedRowModel().rows.map((row) => row.original.id), [table.getSelectedRowModel().rows]);

  const onSelectionChangeRef = useRef(onSelectionChange);
  useEffect(() => {
    onSelectionChangeRef.current = onSelectionChange;
  }, [onSelectionChange]);

  // Sync selection from selectedIds prop only when rows change
  useEffect(() => {
    if (isInitialMount.current) {
      rowsRef.current = rows;
      if (selectedIds.length > 0) {
        isSyncingSelection.current = true;
        const expectedSelection: MRT_RowSelectionState = {};
        selectedIds.forEach((id) => {
          expectedSelection[id.toString()] = true;
        });
        table.setRowSelection(expectedSelection);
        isSyncingSelection.current = false;
      }
      isInitialMount.current = false;
      return;
    }

    const prevRowIds = rowsRef.current.map((r) => r.id).sort((a, b) => a - b);
    const currentRowIds = rows.map((r) => r.id).sort((a, b) => a - b);
    const rowsChanged = prevRowIds.length !== currentRowIds.length || prevRowIds.some((id, idx) => id !== currentRowIds[idx]);

    if (rowsChanged) {
      rowsRef.current = rows;
      isSyncingSelection.current = true;

      const expectedSelection: MRT_RowSelectionState = {};
      selectedIds.forEach((id) => {
        expectedSelection[id.toString()] = true;
      });
      table.setRowSelection(expectedSelection);
      userClickedSelectAll.current = false;
      allRowIdsWhenSelectAll.current = [];

      isSyncingSelection.current = false;
    }
  }, [rows, selectedIds, table]);

  // Track "Select All" state
  useEffect(() => {
    if (isSyncingSelection.current) return;

    const currentIsAllRowsSelected = table.getIsAllRowsSelected();

    if (!prevIsAllRowsSelected.current && currentIsAllRowsSelected) {
      userClickedSelectAll.current = true;
      allRowIdsWhenSelectAll.current = rows.map((row) => row.id);
    }

    if (ids.length === 0) {
      userClickedSelectAll.current = false;
      allRowIdsWhenSelectAll.current = [];
    }

    prevIsAllRowsSelected.current = currentIsAllRowsSelected;
  }, [table.getIsAllRowsSelected(), ids.length, rows]);

  // Notify parent about selection changes
  useEffect(() => {
    if (isSyncingSelection.current) return;
    if (!onSelectionChangeRef.current) return;

    if (userClickedSelectAll.current) {
      const uncheckedIds = allRowIdsWhenSelectAll.current.filter((id) => !ids.includes(id));
      onSelectionChangeRef.current(uncheckedIds, true);
    } else {
      onSelectionChangeRef.current(ids, false);
    }
  }, [ids]);

  return <Table table={table} isLoading={isLoading} height={heightTable} minHeight={150} headRowHeight={48} bodyRowHeight={64} />;
};
